import { fetchAffiliateLinks } from "./affiliateLinks";

/**
 * Whether to offer "Earn by sharing" in the account menu.
 *
 * The menu renders on every page, so this must not become a request on every
 * page load. Two things keep it cheap:
 *
 *   - it is only asked once the member OPENS the menu, not on render
 *   - the answer is remembered per member for the life of the tab
 *
 * Most members of most memberships are not eligible, so the common case is one
 * request, once, answering "no" — and the item never appears.
 */
const answers = new Map();

const keyFor = (token, memberToken) => `${token}::${memberToken}`;

/** The cached answer, or undefined if it has not been asked yet. */
export function peekEligibility(token, memberToken) {
  return answers.get(keyFor(token, memberToken));
}

export async function loadEligibility(token, memberToken) {
  if (!token || !memberToken) {
    return null;
  }

  const key = keyFor(token, memberToken);

  if (answers.has(key)) {
    return answers.get(key);
  }

  try {
    const data = await fetchAffiliateLinks({ token, memberToken });
    const answer = {
      offer: data.can_opt_in || data.links.length > 0,
      hasLinks: data.links.length > 0,
    };
    answers.set(key, answer);

    return answer;
  } catch {
    // Not cached: a network blip should not hide the entry for the rest of
    // the session, and the next menu open will simply ask again.
    return null;
  }
}

/**
 * Forget the cached answer.
 *
 * Called after a member signs up, so the menu stops offering them something
 * they have already done.
 */
export function forgetEligibility(token, memberToken) {
  answers.delete(keyFor(token, memberToken));
}
